import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import StyledButton from '../common/StyledButton';
import api from '../../services/api';
import { useFestival } from '../../context/FestivalContext';

// `/vendor/registration-status` returns an array of vendor entries; each has
// a `registration_step`, an optional countdown for soft holds
// (`soonest_seconds_until_expiry`), and a list of `registrations` (booth
// registrations) so this banner can show a more accurate state than the
// previous version, which assumed the API returned a single object.
//
// This banner has three variants that map onto the three onboarding
// edge cases:
//   - `no_account_progress`: user signed up but no vendor row yet
//   - `new_year`:           returning vendor with no enrollment for the
//                           active festival
//   - `mid_flow`:           there is a non-complete registration_step

const STEP_LABELS = {
  user_info: 'finishing your account',
  vendor_profile: 'completing your vendor profile',
  product_photos: 'uploading your product photos',
  handmade_agreement: 'reviewing the handmade agreement',
  booth_selection: 'choosing your booth(s)',
  pending_approval: 'submitting your application',
  payment: 'paying for your booth',
};

const STEP_DESTINATIONS = {
  user_info: '/vendor/onboarding',
  vendor_profile: '/vendor/onboarding?step=vendor_profile',
  product_photos: '/vendor/onboarding?step=product_photos',
  handmade_agreement: '/vendor/onboarding?step=handmade_agreement',
  booth_selection: '/vendor/onboarding?step=booth_selection',
  pending_approval: '/vendor/onboarding?step=pending_approval',
};

const formatCountdown = (seconds) => {
  if (typeof seconds !== 'number' || seconds <= 0) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'} left`;
  const hours = Math.round(minutes / 60);
  return `about ${hours} hour${hours === 1 ? '' : 's'} left`;
};

const IncompleteRegistration = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [variant, setVariant] = useState(null);
  const [vendor, setVendor] = useState(null);
  const navigate = useNavigate();
  const { selected: selectedFestival } = useFestival();
  const festivalLabel = selectedFestival
    ? selectedFestival.name || `${selectedFestival.year} Gingham Tree`
    : 'this year';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/vendor/registration-status');
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];

        if (list.length === 0) {
          // User signed up but bailed before the Vendor Profile step, so no
          // vendors row exists yet. Surface a CTA to finish account setup.
          setVariant('no_account_progress');
          setVendor(null);
          return;
        }

        const midFlow = list.find((entry) => {
          const step = entry.registration_step;
          const enrollment = entry.enrollment_status;
          if (!step) return false;
          return step !== 'complete' && enrollment !== 'complete';
        });
        if (midFlow) {
          setVariant('mid_flow');
          setVendor(midFlow);
          return;
        }

        const noEnrollmentForYear = list.every(
          (entry) => !entry.registration_step && !entry.enrollment_status
        );
        if (noEnrollmentForYear) {
          setVariant('new_year');
          setVendor(list[0] || null);
          return;
        }

        // Everything is complete for the active festival; nothing to nudge.
        setVariant(null);
        setVendor(null);
      } catch (err) {
        if (!cancelled) {
          setError(
            "We couldn't check your registration progress. Refresh the page to try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleResume = () => {
    if (variant === 'mid_flow' && vendor) {
      const dest = STEP_DESTINATIONS[vendor.registration_step] || '/vendor/onboarding';
      navigate(dest);
      return;
    }
    navigate('/vendor/onboarding');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!variant) return null;

  const stepLabel = vendor?.registration_step
    ? STEP_LABELS[vendor.registration_step] || 'completing your registration'
    : null;
  const countdownLabel = formatCountdown(vendor?.soonest_seconds_until_expiry);
  const heldBoothCount = (vendor?.registrations || []).filter(
    (r) => r.raw_registration_status === 'soft_held'
  ).length;

  let title;
  let body;
  let buttonLabel;
  if (variant === 'no_account_progress') {
    title = 'Finish creating your vendor profile';
    body = (
      <>
        Your account is ready. Click below to fill out your business profile
        and continue your {festivalLabel} registration.
      </>
    );
    buttonLabel = 'Continue Registration';
  } else if (variant === 'new_year') {
    title = `Start your ${festivalLabel} registration`;
    body = (
      <>
        Your vendor profile from a previous year is on file. Update it for{' '}
        <strong>{festivalLabel}</strong> and pick your booths to finish
        registering.
      </>
    );
    buttonLabel = `Begin ${festivalLabel} Registration`;
  } else {
    title = 'Pick up where you left off';
    body = (
      <>
        You were last working on <strong>{stepLabel}</strong>
        {vendor?.business_name ? (
          <>
            {' '}for <strong>{vendor.business_name}</strong>
          </>
        ) : null}
        .
      </>
    );
    buttonLabel = 'Resume Registration';
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        backgroundColor: '#fffbe7',
        border: '1px solid #f0d96a',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, color: '#555' }}>
            {body}
          </Typography>
          {variant === 'mid_flow' && heldBoothCount > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Chip
                size="small"
                color="warning"
                label={`${heldBoothCount} booth${heldBoothCount === 1 ? '' : 's'} held`}
              />
              {countdownLabel ? (
                <Typography variant="caption" color="text.secondary">
                  {countdownLabel} before they're released.
                </Typography>
              ) : null}
            </Stack>
          )}
        </Box>
        <StyledButton variant="contained" onClick={handleResume}>
          {buttonLabel}
        </StyledButton>
      </Stack>
    </Paper>
  );
};

export default IncompleteRegistration;
